import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import { LayoutUtilProps } from "../../../../types";
import { useLayoutPropsUtil } from "../../../../internal/hooks/useLayoutPropsUtil";

import styles from "./TableContainer.module.scss";

/**
 * Props for the TableContainer component
 * @extends LayoutUtilProps
 * @extends ComponentPropsWithoutRef<"div">
 */
export type TableContainerProps = LayoutUtilProps &
  ComponentPropsWithoutRef<"div">;

/**
 * Styled container for tables that includes a scroll container and provides consistent background and border treatment
 * @param props - The props for the TableContainer component
 * @param props.children - The children of the TableContainer component
 * @param props.className - The class name of the TableContainer component
 * @param props.style - The style of the TableContainer component
 * @param props.ref - The ref of the TableContainer component
 */
export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const { children, className, style, ...rest } = componentProps;

    const tableContainerClassNames = cx(className, styles["table-container"]);
    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <div
        style={styleCombined}
        ref={ref}
        {...rest}
        className={tableContainerClassNames}
        data-anv="table-container"
      >
        <div
          className={styles["table-scroll-container"]}
          data-anv="table-scroll-container"
        >
          {children}
        </div>
      </div>
    );
  },
);

TableContainer.displayName = "TableContainer";
